import React, { useRef } from 'react';
import clsx from 'clsx';
import * as Styled from './OCardBrowse.styles';
import { OCardBrowseProps } from './OCardBrowse.types';
import { MMediaImage } from '../../molecules/MMediaImage';
import type { MButtonIconProps } from '../../molecules/MButtonIcon/MButtonIcon.types';
import { MTextBlockBasic } from '../../molecules/MTextBlockBasic';
import { MTextBlockBasicAlignment } from '../../molecules/MTextBlockBasic/MTextBlockBasic.types';
import { ASubheaderSize } from '../../atoms/ASubheader/ASubheader.types';
import { ABodySize, ABodyColor } from '../../atoms/ABody/ABody.types';
import { AIcon } from '../../atoms/AIcon';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import { useTracking } from '../../hooks';
import { AspectRatio, MediaQuery, ResponsiveImageSpec } from '../../utils/ImageUtils';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OCardBrowse';
const TEST_ID = 'web-ui-kit-OCardBrowse';

// Allowed AEM-dialog aspect-ratio strings for OCardBrowse, mapped to the
// internal AspectRatio enum. 3:2 is the default for any unsupported or
// missing value.
const ALLOWED_ASPECT_RATIO_MAP: Record<string, AspectRatio> = {
  '2:1': AspectRatio.STANDARD_HOR,
  '16:9': AspectRatio.WIDE_HOR,
  '3:2': AspectRatio.CLASSIC_HOR,
  '1:1': AspectRatio.SQUARE,
  '2:3': AspectRatio.CLASSIC_VER,
};

const buildRenditionSpecs = (aspectRatioInput?: string): ResponsiveImageSpec[] => {
  const mapped = aspectRatioInput ? ALLOWED_ASPECT_RATIO_MAP[aspectRatioInput] : undefined;
  return [
    {
      media: MediaQuery.MIN_WIDTH_XS,
      aspectRatio: mapped ?? AspectRatio.CLASSIC_HOR,
      imgWidthPx: Styled.CARD_WIDTH_PX,
    },
  ];
};

const OCardBrowse = React.forwardRef<HTMLAnchorElement, OCardBrowseProps>((props, ref) => {
  const {
    ctaLink,
    mMediaImage,
    mTextBlockBasic,
    isAuthorMode,
    trackingProperties,
    custom_click_track_value,
    preTrackVal,
    isExternal,
    className,
    'aria-label': ariaLabel,
    ...rest
  } = props;

  const subheaderText = mTextBlockBasic?.aSubheader?.text;
  const hasRequiredContent = !!ctaLink && !!subheaderText;

  const internalRef = useRef<HTMLAnchorElement>(null);
  const elementRef = (ref ?? internalRef) as React.RefObject<HTMLAnchorElement>;

  useTracking({
    ref: elementRef as React.RefObject<HTMLElement>,
    trackingProperties,
    isExternal,
    custom_click_track_value,
    preTrackVal,
    text: subheaderText,
  });

  if (!hasRequiredContent && !isAuthorMode) return null;

  // Single-click-target enforcement: the card surface is the only interactive
  // element. If a caller (or AEM model) provides mMediaImage.mButtonIcon, render
  // it visually but make it non-interactive (disabled + removed from tab order)
  // so it can never compete with the card's anchor for clicks or focus.
  const disableNestedButton = (buttonIcon: MButtonIconProps | undefined): MButtonIconProps | undefined =>
    buttonIcon ? { ...buttonIcon, disabled: true, tabIndex: -1, 'aria-hidden': true } : undefined;

  const safeMediaImage = mMediaImage
    ? {
        ...mMediaImage,
        mButtonIcon: disableNestedButton(mMediaImage.mButtonIcon),
        item: mMediaImage.item
          ? {
              ...mMediaImage.item,
              mButtonIcon: disableNestedButton(mMediaImage.item.mButtonIcon),
            }
          : undefined,
      }
    : undefined;

  const textBlock = mTextBlockBasic
    ? {
        ...mTextBlockBasic,
        alignment: mTextBlockBasic.alignment ?? MTextBlockBasicAlignment.Left,
        aEyebrow: mTextBlockBasic.aEyebrow
          ? {
              ...mTextBlockBasic.aEyebrow,
              className: clsx(mTextBlockBasic.aEyebrow.className, 'm-ellipsis-1'),
            }
          : undefined,
        aSubheader: mTextBlockBasic.aSubheader
          ? {
              ...mTextBlockBasic.aSubheader,
              size: mTextBlockBasic.aSubheader.size ?? ASubheaderSize.L,
              className: clsx(mTextBlockBasic.aSubheader.className, 'm-ellipsis-2'),
            }
          : undefined,
        aBody: mTextBlockBasic.aBody
          ? {
              ...mTextBlockBasic.aBody,
              size: mTextBlockBasic.aBody.size ?? ABodySize.S,
              color: mTextBlockBasic.aBody.color ?? ABodyColor.Alternate,
              className: clsx(mTextBlockBasic.aBody.className, 'm-ellipsis-3'),
            }
          : undefined,
      }
    : undefined;

  return (
    <Styled.Wrapper
      ref={elementRef}
      className={className}
      aria-label={ariaLabel || subheaderText || undefined}
      {...(ctaLink ? { href: ctaLink } : {})}
      {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...rest}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
    >
      {safeMediaImage && (
        <MMediaImage
          renditionSpecs={buildRenditionSpecs(
            (safeMediaImage.aImage as { aspectRatio?: string } | undefined)?.aspectRatio
          )}
          {...safeMediaImage}
        />
      )}
      <Styled.ContentRow>
        <Styled.TextBlockWrapper>{textBlock && <MTextBlockBasic {...textBlock} />}</Styled.TextBlockWrapper>
        <Styled.IconWrapper>
          <AIcon icon="icon-arrow-right" size={AIconSize.L} aria-hidden="true" />
        </Styled.IconWrapper>
      </Styled.ContentRow>
    </Styled.Wrapper>
  );
});

OCardBrowse.displayName = 'OCardBrowse';

export { OCardBrowse, TEST_ID as O_CARD_BROWSE_TEST_ID };
